export enum ENotificationType {
  NEW_BIT = 'new-bit',
  COMMENT_BIT = 'comment-bit',
  LIKE_BIT = 'like-bit',
  FOLLOW = 'follow',
  SUBSCRIPTION = 'subscription',
  DONATION = 'donation',
  PAYOUT = 'payout',
  APPROVED_PROJECT = 'approved_project',
  ERROR_PROJECT = 'error_project',
  REJECTED_PROJECT = 'rejected_project',
  REVIEW_PROJECT = 'review_project',
  SUPERLIKE_RECEIVED = 'superlike_received',
  SMBD_REPLIED_YOUR_COMMENT = 'smbd_replied_your_comment',
  NEW_COMMENT_ON_YOUR_POST = 'new_comment_on_your_post',
  MENTIONED_IN_POST = 'mentioned_in_post',
  MENTIONED_IN_COMMENT = 'mentioned_in_comment',
}

export enum EHeardFrom {
  PARTNER = 'partner',
  FRIEND_OR_COLLEAGUE = 'friend_or_colleague',
  YOUTUBE = 'youtube',
  TIK_TOK = 'tik_tok',
  META = 'instagram_facebook',
  BLUESKY = 'bluesky',
  TWITTER = 'twitter',
  GOOGLE = 'google_search',
  OTHER = 'other',
}

export enum ERole {
  GAME_DEVELOPER = 'game-developer',
  CONTENT_CREATOR = 'content-creator',
  WRITER_OR_BLOGGER = 'writer-or-blogger',
  GAMER = 'gamer',
}

export enum EProjectStatus {
  PROTOTYPE = 'Prototype',
  RELEASED = 'Released',
}

export enum ESystem {
  MAC = 'Mac',
  LINUX = 'Linux',
  WINDOWS = 'Windows',
  IOS = 'IOS',
  ANDROID = 'Android',
}

export enum EContentType {
  GAME = 'game',
  MOD = 'mod',
  ART = 'art',
  SOUND = 'sound',
}

export enum EMaturityRating {
  EVERYONE = 'everyone',
  EVERYONE_10 = 'ten-plus',
  TEEN = 'teen',
  MATURE = 'mature',
}

export interface ISignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  uniqueLink: string;
  userId?: string;
  fullName?: string;
  countryCode: string;
  roleId?: string;
  heardFrom?: string;
  otherSource?: string;
  partnerCode?: string;
}

export interface ILoginData {
  email: string;
  password: string;
  remember?: boolean;
}

export interface IProfile {
  userId: string;
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  countryCode?: string;
  avatarUrl: string;
  bio: string;
  uniqueLink: string;
  isFollowing: boolean;
  subscribedInfo: ISubscribedInfo;
  subscribersCount: number;
  followersCount: number;
  bitsCount?: number;
  fullName?: string;
  isStripeConnected: boolean;
  stripeAccountId?: string;
  hasTier?: boolean;
  currentTierPrice?: number;
  isNewNotifications: boolean;
  heardFrom: EHeardFrom | string;
  isPartner: boolean;
  role: ERole | null;
  isOnboarded: boolean;
  superLikesUsed: number;
  level: number;
  currentXP: number;
  tokens: number;
  currentCycleStart?: string;
  payment: {
    provider: 'Stripe' | 'Paypal' | null;
    paypalStatus?: PaypalRecipientStatus | null;
    isEnabled: boolean;
  };
}

export interface ISubscribedInfo {
  isSubscribed: boolean;
  subscriptionEndDate: Date | null;
  cancelAtPeriodEnd: boolean;
}

export type TilesType =
  | 'add-note'
  | 'add-social'
  | 'add-image'
  | 'add-video'
  | 'add-custom'
  | 'add-title';

export interface ITile {
  id: number;
  profileId: number;
  type: TilesType;
  x: number;
  y: number;
  w: number;
  h: number;

  mx: number;
  my: number;
  mw: number;
  mh: number;

  i: string;

  title?: string;
  content: string;
  iconName?: 'instagram' | 'discord' | 'bluesky' | 'twitch' | 'steam' | 'itch';
}

type FieldType =
  | 'paragraph'
  | 'h1'
  | 'h2'
  | 'quote'
  | 'numbered-list'
  | 'bulleted-list'
  | 'divider'
  | 'image'
  | 'multi-images'
  | 'embed-video';

export interface IBitField {
  id?: number;
  clientId: string;
  bitId: number;
  type: FieldType;
  content: string;
  order: number;
  level?: number;
  urls?: string[];
  files?: string[];
  serverId?: number;
}

export interface IAuthor {
  id: number;
  name: string;
  avatarUrl: string;
  uniqueLink: string;
  fullName?: string;
  bitsAmount?: number;
  projectsAmount?: number;
  subscribersAmount?: number;
  isPartner?: boolean;
  level?: number;
}
export interface IBitComment {
  id: number;
  bitId: number;
  author: IAuthor;
  content: string;
  createdAt: Date;
  replyTo: number | null;
  replies?: IBitComment[];
}
export interface IBitLike {
  count: number;
  isMyLike: boolean;
}

export enum BitVisibility {
  Public = 'public',
  Private = 'private',
}

export enum BitStatus {
  Draft = 'draft',
  Published = 'published',
  New = 'new',
}

export type Unit = 'B' | 'KB' | 'MB' | 'GB' | 'TB';

export interface IBit {
  id: number;
  title: string;
  tags: string[];
  coverImageUrl: string;
  coverImageFile: string;
  createdAt: Date;
  updatedAt: Date;
  author: IAuthor;
  visibility: string;
  fields: IBitField[];
  comments: IBitComment[];
  commentsCount: number;
  likes: IBitLike;
  slug: string;
  isStripeConnected?: boolean;
  stripeAccountId?: string | null;
  hasAccess: boolean;
  status: BitStatus;
  project?: IProject | null;
  likeCount?: number;
  readAlso?: IBit[];
}

export type UploadStatus =
  | 'initiated'
  | 'pending_scan'
  | 'scanning'
  | 'approved'
  | 'infected'
  | 'error'
  | 'aborted'
  | 'published';

export type EntitlementStatus = 'active' | 'on_hold' | 'revoked' | 'refunded';

export interface IEntitlement {
  id: number;
  projectId: number;
  profileId: number;
  grantedAt: Date | string;
  status: EntitlementStatus;
  stripePaymentIntentId: string | null;
}

export interface IUpload {
  id: number;
  sessionId: string;
  projectId: number | null;
  profileId: number;
  bucket: string;
  objectKey: string;
  contentType: string;
  expectedBytes: number;
  status: UploadStatus;
  scanReport?: unknown | null;
  errorMessage?: string | null;
  checksumSha256?: string | null;
  provider: 'Stripe' | 'Paypal' | null;
  createdAt: Date | string;
  updatedAt: Date | string;
  approvedAt?: Date | string | null;
  infectedAt?: Date | string | null;
}

export interface IProject {
  id: number;
  coverImageUrl: string;
  coverImageFile: string;
  createdAt: Date;
  updatedAt: Date;
  title: string;
  tags: string[];
  slug: string;
  author: {
    id: number;
    fullName: string;
    uniqueLink: string;
    avatarUrl: string;
    stripeAccountId: string | null;
    isPartner: boolean;
    payPalRecipient?: IPaypalRecipient | null;
  };
  shortDescription: string;
  description: string;
  status: EProjectStatus;
  contentType: EContentType;
  systems: ESystem[];
  maturityRating: EMaturityRating;
  price?: number;
  trailers?: string[];
  screenshotsFiles?: string[];
  screenshotsUrls?: string[];
  downloads: number;
  relatedBits?: IBit[];
  rating: {
    average: number;
    total: number;
  };
  hasDownloadAccess?: boolean;
  isWished?: boolean;
  isFollowed?: boolean;
  isMyProject?: boolean;
  isDownloadedByCurrentUser?: boolean;
  isRatedByCurrentUser?: boolean;
  files: IProjectFile[];
  version: string;
  fileSize?: string;
  publishingStatus: EPublicationStatus;
}

export enum EPublicationStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  ERROR = 'error',
  ALL = 'all',
}

export interface IDownloadEvent {
  id: number;
  projectId: number;
  profileId: number;
  fileVersion: string;
  createdAt: Date;
}

export interface IFollower {
  id: number;
  fullName: string;
  uniqueLink: string;
  avatarUrl: string;
  isStripeConnected?: boolean;
  stripeAccountId?: string;
  currentTierPrice?: number;
  isFollowing?: boolean;
  subscribedInfo?: ISubscribedInfo;
  followersCount?: number;
  bitsAmount?: number;
  isPartner?: boolean;
}

export interface ISubscriptionTier {
  id: number;
  stripeId: string;
  name: string;
  price: number;
  stripePriceId: string;
  note: string;
  createdAt: Date;
  creator: IAuthor;
}

export interface ISubscription {
  id?: number;
  stripeSubscriptionId: string;
  subscriberId: number;
  creatorId: number;
  tierId: string;
  priceId: string;
  status:
    | 'incomplete'
    | 'incomplete_expired'
    | 'trialing'
    | 'active'
    | 'canceling'
    | 'past_due'
    | 'canceled'
    | 'unpaid'
    | 'paused';
  startDate: Date;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  endedAt: Date | null;
  createdAt: Date;
}

export interface IPaymentRecord {
  id: number;
  stripePaymentId: string;
  amount: number;
  creatorId: number;
  payerId: number;
  paymentDate: Date;
  isDonation?: boolean;
  donationMessage?: string;
  invoiceUrl: string;
  netAmount?: number;
  paymentType?: 'donation' | 'subscription' | 'purchase';
}
export interface INotification {
  id: string;
  ownerId: string;
  slug?: string;
  donationAmount?: string | number;
  earningsAmount?: string | number;
  type: ENotificationType;
  producer?: IAuthor;
  producerId?: number;
  isNew: boolean;
  createdAt: string;
  reason?: string;
}

export interface IDonationGoal {
  id: number;
  creatorId: number;
  name: string;
  targetAmount: number;
  currentAmount: number;
  description: string;
  startDate: Date;
  createdAt: Date;
  imageUrl?: string;
  endDate?: Date;
}

export interface ICode {
  id: number;
  type: 'partner' | 'reward';
  code: string;
  partnerProfileId: number | null;
  partnerName: string | null;
  rewardDescription: string | null;
  rewardImageUrl: string | null;
  startedAt: Date;
}

export interface IReferralProgram {
  id: number;
  partnerCodeId: number;
  creatorId: number;
  joinedAt: Date;
}

export interface IAchievement {
  id: number;
  title: string;
  description: string;
  iconUrl: string;
  isVisible: boolean;
}

export interface IProfileAchievement {
  id: number;
  profileId: number;
  achievementCodeId: number;
  isVisible: boolean;
  achievedAt: Date;
}

export interface IProjectFile {
  id: number;
  projectId: number;
  fileUrl: string;
  fileSize: string;
  fileVersion: string;
  fileHash: string;
  system: ESystem;
  createdAt: Date;
}

export interface IPayment {
  id: number;
  projectId: number;
  creatorId: number;
  buyerId: number;
  amount: number;
  checkoutSessionId: string;
  paymentIntentId: string;
  chargeId: string;
  status:
    | 'authorized'
    | 'captured'
    | 'cancelled'
    | 'completed'
    | 'refunded'
    | 'partial_refunded'
    | 'failed';
  transferGroup: string;
  holdUntil: Date;
  refundWindowUntil: Date;
  creatorStripeAccountId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface IReview {
  id: number;
  projectId: number;
  project?: IProject;
  authorId: number;
  author?: IAuthor;
  rating: number;
  text: string;
  createdAt: Date;
}

export type StarValue = 1 | 2 | 3 | 4 | 5;
export type RatingsBreakdown = Partial<Record<StarValue, number>>;

export const XP_RULE_CODES = [
  'MEANINGFUL_COMMENT',
  'SUPERLIKE_RECEIVED',
  'SUPERLIKE_GIVEN',
  'REVIEW_PROJECT',
  'LIKE_BIT',
  'FOLLOW_PROFILE',
  'CREATOR_THREAD_REPLY',
  'ACCOUNT_CREATED',
  'REACH_LEVEL_2',
  'REACH_LEVEL_3',
  'DAILY_50XP_BONUS',
  'DAILY_SUPERLIKE_BONUS',
  'DAILY_TKN_BONUS',
];

export type XpRuleCode = (typeof XP_RULE_CODES)[number];

export interface IEconomyModel {
  id: number;
  cycleLengthDays: number;
  cycleAnchorSundayCt: string;
  maxCycleLevel: number;
  level2ThresholdXp: number;
  level3ThresholdXp: number;
  dailyXpBonusThreshold: number;
  giveSuperlikesPerDay: number;
  meta: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export type IgdbGameMeta = {
  igdbId: number;
  name: string;
  summary?: string | null;
  slug?: string | null;
  coverImageUrl?: string | null;
  thumb?: string | null;
  tags?: string[];
  id?: number;
  developer?: string | null;
  publisher?: string | null;
  screenshots?: string[];
  videos?: { source: string; url: string }[];
};

export type ILinkedGame = {
  id: number;
  isIgdbGame: boolean;
  title: string;
  coverImageUrl: string;
  slug?: string;
  description: string;
  tags: string[];
};

export type SocialReaction = 'like' | 'dislike';
export interface ISocialPost {
  id: number;
  author: IAuthor;
  text: string;
  createdAt: Date;
  updatedAt: Date;
  likesCount: number;
  commentsCount: number;
  repliesCount: number;
  youTubeVideoUrl?: string;
  projectId: number | null;
  igdbGameId: number | null;
  gifId: string | null;
  linkedGame?: ILinkedGame;
  uploadedImagesUrl?: string[];
  uploadedVideoUrl?: string;
  isReposted?: boolean;
  repostsCount?: number;
  reactionCounts?: {
    likes: number;
    dislikes: number;
    score: number;
  };
  myReaction?: SocialReaction | null;
}

export interface ISocialPostComment {
  id: number;
  postId: number;
  author: IAuthor;
  parentId: number | null; // null = root, number = rootId
  taggedUserId: number | null; // only for reply
  taggedUserUniqueLink?: string | null; // only for reply
  replyToCommentId: number | null; // only for reply
  isSuperLiked: boolean;
  content: string;
  createdAt: string | Date;
  updatedAt?: string | Date | null;
  deletedAt?: string | Date | null;
  commentsCount?: number; // all comments count (including replies)
  reactionCounts?: { likes: number; dislikes: number; score: number };
  myReaction?: SocialReaction | null;
  repliesCount?: number;
}

export type CommentsCursor = { id: number };

export type CreateSocialPostCommentInput = {
  content: string;
  parentId?: number | null;
  taggedUserId?: number | null;
  replyToCommentId?: number | null;
};

export type SearchGameItem = {
  type: 'project' | 'igdb';
  id: number;
  igdbId?: number;
  title: string;
  slug: string | null;
  coverThumbUrl: string | null;
  coverBigUrl: string | null;
  description: string | null;
  tags: string[];
  source?: 'db' | 'api';
  isIgdbGame: boolean;
  developer?: string | null;
  publisher?: string | null;
  screenshots?: string[];
  videos?: { source: string; url: string }[];
};

export type ISocialFeedItem =
  | {
      type: 'post';
      activityAt: Date;
      post: ISocialPost;
    }
  | {
      type: 'repost';
      activityAt: Date;
      repost: {
        createdAt: Date;
        by: {
          id: number;
          fullName: string | null;
          avatarUrl: string | null;
          uniqueLink: string | null;
          isPartner: boolean | null;
          level: number | null;
        };
      };
      post: ISocialPost;
    };

export type PaypalRecipientStatus =
  | 'unverified'
  | 'active'
  | 'blocked'
  | 'email_verified';

export interface IPaypalRecipient {
  id: number;
  profileId: number;
  paypalEmail: string;
  status: PaypalRecipientStatus;
  lastErrorCode?: string | null;
  lastErrorMessage?: string | null;
  lastPayoutAttemptAt?: string | null;
  createdAt: string;
  updatedAt: string;
  paypalMembershipProductId?: string | null;
}

export type CommitCaptureLedgerArgs = {
  paypalOrderId?: string;
  paypalCaptureId: string;
  amount?: string;
  currency?: string;
  paypalFee?: string;
};

export type Period = 'allTime' | 'weekly' | 'monthly';
